<template>
  <div :class="{ 'is-active': active, disabled }" class="ui-collapse">
    <div
      :id="`trigger-${collapseId}`"
      role="button"
      :aria-controls="`content-${collapseId}`"
      :aria-expanded="active ? 'true' : 'false'"
      class="ui-collapse-header"
      @click.stop="toggle"
    >
      <span class="ui-collapse-icon start">
        <slot name="ui-collapse-icon-start" />
      </span>
      <span class="ui-collapse-title">
        <slot name="ui-collapse-title" />
      </span>
      <span class="ui-collapse-subtitle">
        <slot name="ui-collapse-subtitle" />
      </span>
      <slot name="ui-collapse-icon">
        <span class="ui-collapse-icon end"
          ><span class="chevron-l" /><span class="chevron-r"
        /></span>
      </slot>
    </div>
    <transition name="fade" tag="div">
      <div
        v-show="active"
        :id="`content-${collapseId}`"
        :aria-labelledby="`trigger-${collapseId}`"
        :aria-hidden="!active"
        class="ui-collapse-content"
      >
        <slot name="ui-collapse-body" />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    selected?: boolean
    disabled?: boolean
    collapseId: string
  }>(),
  {
    selected: false,
    disabled: false,
  },
)

const emit = defineEmits<{
  'collapse-open': [index: string]
  'collapse-close': [index: string]
}>()

const active = ref(false)

watch(
  () => props.selected,
  (selected) => {
    active.value = selected
  },
)

watch(active, (isActive) => {
  isActive
    ? emit('collapse-open', props.collapseId)
    : emit('collapse-close', props.collapseId)
})

const toggle = () => {
  active.value = !active.value
}

// Handle initial state
onMounted(async () => {
  await nextTick(() => {
    if (active.value !== props.selected) {
      active.value = props.selected
    }
  })
})
</script>

<style scoped>
.ui-collapse {
  border-bottom: 1px solid var(--colour-page-background);
}

.ui-collapse-header:active {
  background: rgba(0, 0, 0, 0.4);
}

.ui-collapse-header {
  font-weight: bold;
  color: #333;
  text-decoration: none;
  padding: 1em;
  min-height: 3em;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}

.ui-collapse-title {
  flex-grow: 1;
}

.ui-collapse-subtitle {
  font-weight: normal;
  font-size: var(--body);
  color: var(--colour-text-light);
}

.ui-collapse-content {
  border-top: 1px dashed var(--colour-page-background);
}

.ui-collapse-icon {
  flex-shrink: 0;
  align-self: center;
  display: block;
}

.ui-collapse-icon {
  max-width: 2em;
  height: auto;
}

.ui-collapse-icon :deep(img) {
  width: 100%;
  height: auto;
  max-height: 2.2em;
}

.ui-collapse-icon.start {
  max-width: 2.8em;
}

.ui-collapse-icon.start > :deep(div) {
  margin-right: 0.8em;
}

.ui-collapse-subtitle :deep(.item-count) {
  display: inline-block;
  min-width: 1.7em;
  min-height: 1.6em;
  border-radius: 2em;
  background-color: var(--colour-pale-blue);
  padding: 0.2em;
  text-align: center;
}

:deep(.ui-collapse-icon.end) {
  display: flex;
  align-items: center;
  width: 1.7em;
  height: 1.7em;
  margin-left: 0.5em;
}

:deep(.ui-collapse-icon.end span) {
  display: block;
  border-radius: 0.2em;
  height: 0.2em;
  width: 100%;
  transition: transform 0.3s;
  border: 0.1em solid #999;
  background: #999;
}

:deep(.chevron-l) {
  transform: rotate(45deg) translate(0.25em, 0);
}

:deep(.chevron-r) {
  transform: rotate(-45deg) translate(-0.25em, 0);
}

.is-active > :deep(.ui-collapse-header .ui-collapse-icon.end .chevron-l) {
  transform: rotate(-45deg) translate(0, 0.25em);
}

.is-active > :deep(.ui-collapse-header .ui-collapse-icon.end .chevron-r) {
  transform: rotate(45deg) translate(0, 0.25em);
}

.disabled {
  filter: grayscale(1);
  opacity: 0.3;
  pointer-events: none;
}
</style>
